import React from "react";
import { Building2, Home, Info, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAP_IMAGE = "https://customer-assets.emergentagent.com/job_senior-balance-rehab/artifacts/kl1w108m_ChatGPT%20Image%20Jun%2027%2C%202026%2C%2011_02_24%20AM.png";

const services = [
  { label: "Initial Assessment", price: "£80", note: "Comprehensive 60-min assessment" },
  { label: "Follow-Up Session", price: "£70", note: "Tailored treatment & progression" },
];

const zones = [
  {
    name: "Standard Area",
    fee: "No additional travel fee",
    postcodes: "RG1 – RG4, RG30 – RG31",
    dot: "bg-rose-300",
  },
  {
    name: "Extended Area",
    fee: "Additional travel fee: £10 – £20",
    postcodes: "RG2, RG7, RG8, RG10, RG18, RG19, RG41, RG42",
    dot: "bg-amber-200",
  },
  {
    name: "Further Area",
    fee: "Additional travel fee: £20 – £30",
    postcodes: "Areas outside the zones shown on the map",
    dot: "bg-sky-200",
  },
];

export default function Pricing() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="pricing"
      className="py-20 md:py-32 px-6 md:px-12 bg-brand-cream"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-brand-terracotta" data-testid="pricing-eyebrow">
            Honest pricing
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-brand-blue mt-4 leading-tight" data-testid="pricing-headline">
            Clear, transparent rates.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-6">
            We charge by the session — no packages, no pressure. Choose to visit us at the clinic in Reading
            or book a home visit.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Clinic appointments */}
          <div
            className="bg-white rounded-3xl p-8 md:p-10 border border-brand-clay flex flex-col"
            data-testid="pricing-clinic-card"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                <Building2 size={24} strokeWidth={1.8} />
              </span>
              <div>
                <h3 className="font-display text-2xl md:text-3xl text-brand-blue">Clinic Appointments</h3>
                <p className="text-sm text-slate-500">At our Reading clinic · RG30 4LR</p>
              </div>
            </div>

            <ul className="divide-y divide-brand-clay" data-testid="pricing-clinic-list">
              {services.map((s) => (
                <li key={s.label} className="py-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-display text-xl text-brand-blue">{s.label}</p>
                    <p className="text-sm text-slate-500 mt-1">{s.note}</p>
                  </div>
                  <span className="font-display text-3xl font-bold text-brand-blue shrink-0">{s.price}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => scrollTo("contact")}
              className="mt-8 self-start bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full px-8 h-12 text-base font-semibold"
              data-testid="pricing-clinic-cta"
            >
              Book Clinic Appointment
            </Button>
          </div>

          {/* Home visits */}
          <div
            className="bg-white rounded-3xl p-8 md:p-10 border border-brand-clay flex flex-col"
            data-testid="pricing-home-card"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-12 rounded-xl bg-brand-terracotta/15 text-brand-terracotta flex items-center justify-center">
                <Home size={24} strokeWidth={1.8} />
              </span>
              <div>
                <h3 className="font-display text-2xl md:text-3xl text-brand-blue">Home Visits</h3>
                <p className="text-sm text-slate-500">Across Reading &amp; Berkshire</p>
              </div>
            </div>

            <ul className="divide-y divide-brand-clay" data-testid="pricing-home-list">
              {services.map((s) => (
                <li key={s.label} className="py-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-display text-xl text-brand-blue">{s.label}</p>
                    <p className="text-sm text-slate-500 mt-1">{s.note}</p>
                  </div>
                  <span className="font-display text-3xl font-bold text-brand-blue shrink-0">{s.price}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm text-slate-600 leading-relaxed">
              Plus a travel fee depending on distance and travel time from our clinic — see areas and charges below.
              Any additional charges will always be discussed and agreed with you before your appointment is confirmed.
            </p>

            <Button
              onClick={() => scrollTo("contact")}
              variant="outline"
              className="mt-6 self-start border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white rounded-full px-8 h-12 text-base font-semibold"
              data-testid="pricing-home-cta"
            >
              Request Home Visit Quote
            </Button>
          </div>
        </div>

        {/* Home visit areas & charges */}
        <div className="mt-12 lg:mt-16 bg-white rounded-3xl border border-brand-clay overflow-hidden" data-testid="home-visit-area-card">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-5 p-8 md:p-10 flex flex-col">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                  <MapPin size={24} strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-2xl md:text-3xl text-brand-blue leading-tight">Home Visit Areas &amp; Charges</h3>
              </div>

              <p className="text-base text-slate-700 leading-relaxed">
                We provide home visits across Reading and surrounding areas. Travel fees apply for locations
                outside our standard area.
              </p>

              <ul className="mt-7 space-y-5">
                {zones.map((z) => (
                  <li key={z.name} className="flex items-start gap-3" data-testid={`zone-${z.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <span className={`w-4 h-4 rounded-sm mt-1 shrink-0 ${z.dot}`} aria-hidden />
                    <div>
                      <p className="font-display text-lg text-brand-blue">{z.name}</p>
                      <p className="text-sm font-semibold text-brand-terracotta mt-0.5">{z.fee}</p>
                      <p className="text-sm text-slate-600 mt-1">{z.postcodes}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-6 border-t border-brand-clay flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                <Info size={18} strokeWidth={1.8} className="text-brand-teal shrink-0 mt-0.5" />
                <p>
                  Travel fees are based on distance and travel time. Any additional charges will be confirmed
                  before your appointment is booked.
                </p>
              </div>

              <p className="mt-7 font-display text-lg text-brand-blue">
                Please contact us for a personalised quote for your home visit.
              </p>

              <Button
                onClick={() => scrollTo("contact")}
                className="mt-5 self-start bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full px-8 h-12 text-base font-semibold"
                data-testid="home-visit-quote-cta"
              >
                Get Personalised Quote
              </Button>
            </div>

            <div className="lg:col-span-7 bg-brand-sand flex items-center justify-center p-4 md:p-8">
              <img
                src={MAP_IMAGE}
                alt="Map of Reading showing home visit coverage zones — Standard, Extended and Further Areas"
                className="w-full h-auto rounded-2xl"
                data-testid="home-visit-map"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
